// SCR-007: one customer-month's invoice (/customers/:orgId/invoices/:period): the customer invoice
// (internal: price, AWS cost, profit per line), the statement the customer sees, and the ledger
// invoice underneath (billing/ledger-invoice.tsx). A `not_billed` customer-month or an
// actual-basis month awaiting CUR finalization still show their ledger invoice; only the
// customer-invoice and statement panels take the named states instead of a table.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { useCustomerName } from "../../ui/customer-directory";
import { LabelText } from "@nexus/ui";
import { formatPercentBp } from "@nexus/ui";
import { isMonth, longMonthLabel } from "@nexus/ui";
import { Card, Chip, NameCell, PageHeader, Segmented, StatCard, StatGrid } from "@nexus/ui";
import { Link, useBreadcrumb } from "../../ui/shell";
import { Empty, ErrorBanner, Loading } from "@nexus/ui";
import { Money } from "@nexus/ui";
import { LedgerInvoice } from "../billing/ledger-invoice";
import { customerPath } from "../orgs/model";
import { adjustmentsPath, NOT_BILLED_MESSAGE } from "../terms/model";
import {
  CUSTOMER_INVOICE_PATH,
  EXPENSE_BASIS_LABELS,
  EXPENSE_BASIS_OPTIONS,
  finalBadgeLabel,
  invoiceLineRow,
  invoiceTotals,
  loadCustomerInvoice,
  type CustomerInvoice,
  type ExpenseBasisKind,
  type InvoiceState,
} from "./model";
import { StatementPreview } from "./statement-preview";

export function CustomerInvoicePage({ params }: { params: Record<string, string>; query: URLSearchParams }) {
  const orgId = params.orgId ?? "";
  const period = params.period ?? "";
  const client = useApi();
  const name = useCustomerName(orgId);
  const monthText = isMonth(period) ? longMonthLabel(period) : period;
  const [basis, setBasis] = useState<ExpenseBasisKind>("list");
  const [state, setState] = useState<InvoiceState>({ kind: "loading" });

  useBreadcrumb([{ label: name, href: customerPath(orgId, "billing") }, { label: monthText }]);

  const reload = () => {
    if (!orgId || !period) return;
    setState({ kind: "loading" });
    void loadCustomerInvoice(client, orgId, period, basis).then(setState);
  };

  useEffect(reload, [client, orgId, period, basis]);

  return (
    <div class="page">
      <PageHeader
        title={`${monthText} invoice`}
        titleExtra={state.kind === "loaded" && <Chip tone={state.invoice.final ? "grey" : "blue"}>{finalBadgeLabel(state.invoice.final)}</Chip>}
        lede="The customer invoice with cost and profit, the statement the customer sees, and the metered ledger underneath."
        meta={
          <>
            <NameCell name={name} id={orgId} href={customerPath(orgId, "billing")} />
            <Link href={adjustmentsPath(orgId, period)}>Usage adjustments</Link>
          </>
        }
        actions={
          <div class="field field-compact">
            <LabelText id="invoice-basis-label" label="Cost basis" help="common.costBasis" compact />
            <Segmented
              labelledBy="invoice-basis-label"
              small
              options={EXPENSE_BASIS_OPTIONS.map((option) => ({ value: option, label: EXPENSE_BASIS_LABELS[option] }))}
              value={basis}
              onChange={(value: ExpenseBasisKind) => setBasis(value)}
            />
          </div>
        }
      />

      <Card
        title="Customer invoice"
        help="terms.customerInvoice"
        actions={
          state.kind === "loaded" && (
            <>
              <a class="button button-secondary" href={client.csvUrl(CUSTOMER_INVOICE_PATH, { path: { orgId, period }, query: { basis } })} download>
                Download CSV
              </a>
              <a class="button button-secondary" href={client.pdfUrl(CUSTOMER_INVOICE_PATH, { path: { orgId, period } })} download>
                Download PDF
              </a>
            </>
          )
        }
      >
        {state.kind === "loading" && <Loading label="Loading customer invoice…" />}
        {state.kind === "not-billed" && (
          <Empty
            message={`${NOT_BILLED_MESSAGE} ${name} is not billed for ${monthText}. Set who pays whom on the customer's Billing tab.`}
            action={<Link href={customerPath(orgId, "billing")}>Open the Billing tab</Link>}
          />
        )}
        {state.kind === "cur-not-final" && (
          <Empty message="Awaiting the AWS bill (CUR) for this month — the customer invoice and statement are written once it is final." />
        )}
        {state.kind === "absent" && <Empty message="This month closed before invoices were written; there is no customer invoice for it." />}
        {state.kind === "error" && <ErrorBanner error={state.error} onRetry={reload} />}
        {state.kind === "loaded" && <InvoicePanel invoice={state.invoice} />}
      </Card>

      <StatementPreview orgId={orgId} period={period} customer={name} monthText={monthText} />

      <LedgerInvoice orgId={orgId} period={period} />
    </div>
  );
}

function InvoicePanel({ invoice }: { invoice: CustomerInvoice }) {
  const totals = invoiceTotals(invoice);
  const cur = invoice.basis === "cur";
  return (
    <>
      <StatGrid label="Invoice totals">
        <StatCard label="Billed" help="common.billed" value={<Money micro={invoice.priceMicroUsd} />} />
        <StatCard label="AWS list cost" help="common.awsCost" value={<Money micro={invoice.listCostMicroUsd} />} />
        <StatCard label="Profit" help="common.grossProfit" accent value={<Money micro={invoice.marginMicroUsd} />} />
        <StatCard
          label="Markup and margin"
          help="common.markupAndMargin"
          value={`${formatPercentBp(totals.markupBp)} markup`}
          note={`${formatPercentBp(totals.marginBp)} margin — the share of the bill kept.`}
        />
        {cur && (
          <StatCard
            label="Actual AWS bill"
            help="common.costBasis"
            value={invoice.expenseMicroUsd === null ? "—" : <Money micro={invoice.expenseMicroUsd} />}
            note={invoice.actualMarginMicroUsd === null ? "Not known yet." : <>Profit over it: <Money micro={invoice.actualMarginMicroUsd} /></>}
          />
        )}
      </StatGrid>
      {totals.markupBasisNote && <p class="card-note">{totals.markupBasisNote}</p>}
      {totals.provisional && <p class="banner banner-warning">Provisional — the actual AWS bill for this month is not final yet.</p>}
      <div class="table-scroll">
        <table class="data-table">
          <caption class="visually-hidden">Customer invoice lines</caption>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Line</th>
              <th scope="col">Model</th>
              <th scope="col">Class</th>
              <th scope="col" class="numeric">
                Tokens
              </th>
              <th scope="col" class="numeric">
                AWS cost
              </th>
              {cur && (
                <th scope="col" class="numeric">
                  Actual AWS bill
                </th>
              )}
              <th scope="col" class="numeric">
                Billed
              </th>
              <th scope="col" class="numeric">
                Profit
              </th>
              <th scope="col">Rule</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line) => {
              const row = invoiceLineRow(line);
              return (
                <tr key={row.lineNo}>
                  <td>{row.lineNo}</td>
                  <td>{row.kindLabel}</td>
                  <td class="mono">{row.model}</td>
                  <td>{row.tokenClass}</td>
                  <td class="numeric">{row.tokens}</td>
                  <td class="numeric cell-cost">{row.costDisplay ? <span title={row.costDisplay.exact}>{row.costDisplay.text}</span> : "—"}</td>
                  {cur && <td class="numeric cell-cost">{row.expenseDisplay ? <span title={row.expenseDisplay.exact}>{row.expenseDisplay.text}</span> : "—"}</td>}
                  <td class="numeric">{row.unpriced ? "Unpriced" : row.priceDisplay ? <span title={row.priceDisplay.exact}>{row.priceDisplay.text}</span> : "—"}</td>
                  <td class="numeric cell-profit">
                    {row.marginDisplay ? <span title={row.marginDisplay.exact}>{row.marginDisplay.text}</span> : "—"}
                    {row.negativeMargin && <span class="negative-margin-label"> (negative margin)</span>}
                  </td>
                  <td>{row.ruleText}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" colSpan={5}>
                Total
              </th>
              <td class="numeric cell-cost">
                <Money micro={invoice.listCostMicroUsd} />
              </td>
              {cur && <td class="numeric cell-cost">{invoice.expenseMicroUsd === null ? "—" : <Money micro={invoice.expenseMicroUsd} />}</td>}
              <td class="numeric">
                <Money micro={invoice.priceMicroUsd} />
              </td>
              <td class="numeric cell-profit">
                <Money micro={invoice.marginMicroUsd} />
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {totals.creditCarriedOutDisplay && <p class="card-note">Credit carried to the next invoice: {totals.creditCarriedOutDisplay.text}.</p>}
    </>
  );
}
